import React from 'react';

import { useCurrentPlatformUsageInformationQuery } from '@dashboard/generated/wp/platform';
import { DataUsageOutlined } from '@mui/icons-material';
import { Alert, AlertTitle, LinearProgress, Stack, Typography } from '@mui/material';

import { CurrencyFormatter, DateFormatter, NumberFormatter } from '@waypoint/react-formatters';

export default function UsageReport() {
  const { data } = useCurrentPlatformUsageInformationQuery();
  const info = data?.currentPlatformUsageInformation;
  if (!info) {
    return null;
  }

  const overage = info.overage;

  const renderNotice = () => {
    if (overage <= 0) {
      return null;
    }
    return (
      <Alert icon={<DataUsageOutlined fontSize="inherit" />} severity="warning">
        <AlertTitle>Overages notice</AlertTitle>
        <Typography variant="body2">
          You are currently over the base email limit on your plan. Additional emails on this plan are{' '}
          <strong>
            <CurrencyFormatter value={info.overageCostPerThousand / 100} currency="USD" /> per 1,000 emails
          </strong>{' '}
          and will be billed as part of your next invoice.
        </Typography>
      </Alert>
    );
  };

  const renderProgressBar = () => {
    if (overage > 0) {
      return <LinearProgress variant="determinate" value={100} color="warning" sx={{ height: 12 }} />;
    }
    return (
      <LinearProgress
        variant="determinate"
        value={(info.totalUsage / info.baseTierLimit) * 100}
        color="success"
        sx={{ height: 12 }}
      />
    );
  };

  const renderRemainingCount = () => {
    if (overage > 0) {
      return (
        <>
          <NumberFormatter value={overage} /> overage
        </>
      );
    }
    return (
      <>
        <NumberFormatter value={info.baseTierLimit - info.totalUsage} /> remaining (before overages)
      </>
    );
  };

  return (
    <Stack spacing={2}>
      <Stack
        justifyContent="space-between"
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'left', md: 'center' }}
      >
        <Typography variant="overline" color="text.secondary">
          Usage
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Cycle: <DateFormatter value={info.periodStart} template="MMM D" /> to{' '}
          <DateFormatter value={info.periodEnd} template="MMM D" />
        </Typography>
      </Stack>
      <Stack spacing={1}>
        {renderProgressBar()}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            <NumberFormatter value={info.totalUsage} /> / <NumberFormatter value={info.baseTierLimit} />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {renderRemainingCount()}
          </Typography>
        </Stack>
      </Stack>
      {renderNotice()}
    </Stack>
  );
}
